import React from 'react';
import PropTypes from 'prop-types';

/**
 * @template P
 * @template S
 * @extends {React.Component<P, S>}
 */
class BusComponent extends React.Component {
    /** @param {P} props */
    constructor(props, context) {
        super(props, context);

        this._gluBusEvents = {};
    }

    bindGluBusEvents(events) {
        this._busBound = true;
        Object.keys(events).forEach(eventName => {
            const handler = events[eventName];
            // Wrap the handler so it becomes a no-op once the component has
            // unbound (i.e. unmounted). If the underlying glu bus ever fails
            // to remove a listener on `off`, a detached instance would still
            // receive events and call setState on a dead component — which
            // desyncs React 15's batched-update pipeline and surfaces as the
            // "performUpdateIfNecessary: Unexpected batch number" warning
            // flood (and intermittently drops controlled-input updates, so
            // text can't be deleted). The guard makes that impossible.
            const guardedHandler = (...args) => {
                if (this._busBound) {
                    handler.apply(this, args);
                }
            };
            this._gluBusEvents[eventName] = guardedHandler;
            this.bus.on(eventName, guardedHandler, this);
        });
    }

    unbindGluBusEvents() {
        this._busBound = false;
        Object.keys(this._gluBusEvents).forEach(eventName => {
            this.bus.off(eventName, this._gluBusEvents[eventName], this);
            delete this._gluBusEvents[eventName];
        });
    }

    emit(eventName, payload) {
        const newPayload = payload || {};

        try {
            if (!this.bus || typeof this.bus.emit !== 'function') {
                return false;
            }

            const result = this.bus.emit(eventName, newPayload);

            // dragonfly sometimes returns undefined instead of false
            return !!result;
        } catch (err) {
            // swallow ONLY "no listeners" crashes
            if (err && typeof err.message === 'string' && err.message.includes("reading 'call'")) {
                // optional debug:
                // console.warn('[BusComponent] emit with no listeners:', eventName);
                return false;
            }

            // real errors should still surface
            throw err;
        }
    }

    get bus() {
        return this.context.bus;
    }
}

BusComponent.contextTypes = {
    viewCode: PropTypes.string,
    isDefaultProject: PropTypes.bool,
    container: PropTypes.string,
    appGuid: PropTypes.string,
    bus: PropTypes.object,
    isPhoneDevice: PropTypes.bool,
    isEmbedded: PropTypes.bool,
    isMobileDevice: PropTypes.bool,
    isIframe: PropTypes.bool,
    isCondensedLayout: PropTypes.bool,
    applicationMode: PropTypes.string,
    onError: PropTypes.func,
    frame: PropTypes.string,
    location: PropTypes.object,
    locationAnalysis: PropTypes.object,
};

export default BusComponent;
